// Interview brief: a script kind whose document is a run of titled fields.
//
// Each field is a `briefField` block node inside the ordinary ProseMirror
// document (doc_json), carrying its identity and audience as attributes. That
// keeps autosave, version snapshots, comments, suggestions, diff, bundles and
// duplicate working with no new plumbing. The one new rule, and the one this
// feature stands or falls on: a field tagged `internal` (or switched off)
// never leaves the worker. `projectBriefForClient` is the single place that
// enforces it; every client-facing payload carrying doc_json goes through it.
//
// Plan: docs/scratchpad-and-interview-brief-plan.md §2. Mock-up:
// mockups/interview-brief.html.

import { ulid } from './ulid';
import { DEFAULT_AUDIO_TYPE } from './audioTypes';
import { DEFAULT_VISUAL_TYPE } from './visualTypes';
import { chooseWarmups, warmupBody } from './warmupBank';

export const BRIEF_TEMPLATE_KEY = 'interview_brief';
export const EMPTY_DOC_JSON = '{"type":"doc","content":[]}';

export type BriefAudience = 'internal' | 'client';
export type BriefBodyKind = 'rich' | 'questions' | 'rows';

export const BRIEF_AUDIENCES: readonly BriefAudience[] = ['internal', 'client'];

export const BRIEF_AUDIENCE_META: Record<
  BriefAudience,
  { label: string; gloss: string }
> = {
  internal: {
    label: 'Internal',
    gloss: 'Only you. Never in the client page and never in their PDF.',
  },
  client: {
    label: 'Client',
    gloss: "On the review page and in the client's PDF.",
  },
};

/** Key of a custom (user-added) field. Built-ins carry a library key. */
export const CUSTOM_FIELD_KEY = 'custom';

export const BRIEF_FIELD_NODE = 'briefField';

/** Attributes carried by every `briefField` node. */
export interface BriefFieldAttrs {
  /** ULID, stable for the life of the field (survives hide/show/reorder). */
  id: string;
  /** Library key for built-ins, `'custom'` for user-added fields. */
  key: string;
  title: string;
  /** Short structured name, meaningful only on the talent field. */
  talent_name: string;
  audience: BriefAudience;
  /** false = hidden from the document and every audience; content kept. */
  enabled: boolean;
  /** true = shown on the shoot view's Brief tab (phone). Never widens audience: an internal field with shoot on is only on the admin's shoot view. */
  shoot: boolean;
  body_kind: BriefBodyKind;
}

export interface BriefFieldDefinition {
  key: string;
  title: string;
  /** Whether a new brief has this field switched on. */
  defaultEnabled: boolean;
  /** Whether the field sits on the shoot view's Brief tab by default. */
  defaultShoot: boolean;
  audience: BriefAudience;
  body_kind: BriefBodyKind;
  /** One-line placeholder seeded into the body when the brief is created. */
  seed: string;
}

/**
 * The built-in field library, in the order a new brief is seeded. Thirteen
 * fields; six on by default. There is deliberately no shoot / logistics
 * field: that material lives in the schedule and call sheet tool.
 */
export const BRIEF_FIELD_LIBRARY: readonly BriefFieldDefinition[] = [
  {
    key: 'purpose',
    title: 'Video purpose & messaging',
    defaultEnabled: true,
    defaultShoot: true,
    audience: 'client',
    body_kind: 'rich',
    seed: 'What is this video for, where will it live, and what should the viewer feel or do after watching?',
  },
  {
    key: 'audience',
    title: 'Audience',
    defaultEnabled: true,
    defaultShoot: false,
    audience: 'client',
    body_kind: 'rich',
    seed: 'Who is watching, what do they already believe, and what are they cautious about?',
  },
  {
    key: 'key_messages',
    title: 'Key messages',
    defaultEnabled: true,
    defaultShoot: true,
    audience: 'client',
    body_kind: 'rich',
    seed: 'The one thing they must remember.',
  },
  {
    key: 'talent',
    title: 'Talent profile',
    defaultEnabled: true,
    defaultShoot: false,
    audience: 'client',
    body_kind: 'rich',
    seed: 'Who is on camera, their role, and how comfortable they are being interviewed.',
  },
  {
    key: 'warmup',
    title: 'Warm-up',
    defaultEnabled: true,
    defaultShoot: true,
    audience: 'internal',
    body_kind: 'questions',
    seed: "How's your day been so far?",
  },
  {
    key: 'questions',
    title: 'Interview questions',
    defaultEnabled: true,
    defaultShoot: false,
    audience: 'client',
    body_kind: 'questions',
    seed: 'Tell me about yourself and what you do.',
  },
  {
    key: 'product',
    title: 'Product / service notes',
    defaultEnabled: false,
    defaultShoot: false,
    audience: 'client',
    body_kind: 'rich',
    seed: 'What the product or service actually does, in the words the talent would use.',
  },
  {
    key: 'tone',
    title: 'Tone, style & format',
    defaultEnabled: false,
    defaultShoot: false,
    audience: 'client',
    body_kind: 'rich',
    seed: 'How it should feel, how long it runs, and where it will be shown.',
  },
  {
    key: 'story_beats',
    title: 'Talent experiences & story beats',
    defaultEnabled: false,
    defaultShoot: false,
    audience: 'internal',
    body_kind: 'rich',
    seed: 'The moments to steer towards, in rough order, and anything to handle gently.',
  },
  {
    key: 'transcript',
    title: 'Pre-interview transcript',
    defaultEnabled: false,
    defaultShoot: false,
    audience: 'internal',
    body_kind: 'rich',
    seed: 'Paste the pre-interview call here.',
  },
  {
    key: 'sensitivities',
    title: 'Do not say / sensitivities',
    defaultEnabled: false,
    defaultShoot: false,
    audience: 'internal',
    body_kind: 'rich',
    seed: 'Names, claims and topics to avoid on camera.',
  },
  {
    key: 'broll',
    title: 'B-roll & visual wishlist',
    defaultEnabled: false,
    defaultShoot: false,
    audience: 'client',
    body_kind: 'rich',
    seed: 'Places, actions and details worth filming around the interview.',
  },
  {
    key: 'narration',
    title: 'Narration / B-roll script',
    defaultEnabled: false,
    defaultShoot: false,
    audience: 'client',
    body_kind: 'rows',
    seed: 'Optional voiced intro or B-roll passage.',
  },
];

/** Keys of the built-in fields, in library order. */
export const BRIEF_FIELD_KEYS: readonly string[] = BRIEF_FIELD_LIBRARY.map((f) => f.key);

export function isBriefTemplate(templateKey: unknown): boolean {
  return templateKey === BRIEF_TEMPLATE_KEY;
}

export function isBriefAudience(value: unknown): value is BriefAudience {
  return value === 'internal' || value === 'client';
}

export function isBriefBodyKind(value: unknown): value is BriefBodyKind {
  return value === 'rich' || value === 'questions' || value === 'rows';
}

export function isBuiltInFieldKey(key: unknown): boolean {
  return typeof key === 'string' && key !== CUSTOM_FIELD_KEY && BRIEF_FIELD_KEYS.includes(key);
}

export function getBriefFieldDefinition(key: string): BriefFieldDefinition | undefined {
  return BRIEF_FIELD_LIBRARY.find((f) => f.key === key);
}

export function defaultShoot(key: string): boolean {
  return getBriefFieldDefinition(key)?.defaultShoot ?? false;
}

/**
 * Coerce a node's raw attrs into a well-formed `BriefFieldAttrs`. Tolerant on
 * purpose: a doc saved by an older build, or a hand-edited one, still renders
 * rather than crashing the editor. Unknown audiences fall back to `internal`,
 * missing ids stay empty for the editor to heal, and only a literal `true`
 * enables a field. These defaults fail closed so malformed attrs cannot leak.
 * Unlike enabled, shoot defaults depend on the sibling key. Old documents
 * have no shoot attribute, so absence means the default for that key, never
 * false. Explicit booleans always win, including false on purpose.
 */
export function normalizeBriefFieldAttrs(raw: unknown): BriefFieldAttrs {
  const a = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const key = typeof a.key === 'string' && a.key ? a.key : CUSTOM_FIELD_KEY;
  const def = getBriefFieldDefinition(key);
  return {
    id: typeof a.id === 'string' && a.id ? a.id : '',
    key,
    title:
      typeof a.title === 'string' && a.title.trim()
        ? a.title
        : def?.title ?? 'Untitled field',
    talent_name: key === 'talent' && typeof a.talent_name === 'string'
      ? Array.from(a.talent_name.replace(/\s+/g, ' ').trim()).slice(0, 80).join('').trim()
      : '',
    audience: key === 'warmup' ? 'internal' : isBriefAudience(a.audience) ? a.audience : 'internal',
    enabled: a.enabled === true,
    shoot: typeof a.shoot === 'boolean' ? a.shoot : defaultShoot(key),
    body_kind: key === 'warmup' ? 'questions' : isBriefBodyKind(a.body_kind) ? a.body_kind : def?.body_kind ?? 'rich',
  };
}

// ---------------------------------------------------------------------------
// Walking the document
// ---------------------------------------------------------------------------

interface DocNode {
  type?: string;
  attrs?: Record<string, unknown> | null;
  content?: DocNode[];
  text?: string;
}

/**
 * Store names only on named talent fields. Admin saves may supply the stored
 * document so older editors that omit the attr preserve it by field id.
 * Explicit empty values still clear a name. Never change the editor defaults:
 * their empty string distinguishes a clear from an older schema's omission.
 */
export function canonicalizeBriefJson(docJson: string, storedJson?: string): string {
  let doc: DocNode;
  try {
    doc = JSON.parse(docJson);
  } catch {
    return docJson;
  }
  if (!docHasBriefFields(doc) && doc?.type !== BRIEF_FIELD_NODE) return docJson;

  const storedNames = new Map<string, string>();
  if (storedJson) {
    try {
      for (const { attrs } of listBriefFields(JSON.parse(storedJson))) {
        if (attrs.key === 'talent' && attrs.id && attrs.talent_name) {
          storedNames.set(attrs.id, attrs.talent_name);
        }
      }
    } catch {
      // Unreadable stored data cannot supply a name.
    }
  }
  const walk = (node: DocNode): DocNode => {
    if (!node || typeof node !== 'object') return node;
    let result = node;
    if (node.type === BRIEF_FIELD_NODE && node.attrs) {
      const attrs = { ...node.attrs };
      if (attrs.key === 'talent' && !Object.prototype.hasOwnProperty.call(attrs, 'talent_name')) {
        attrs.talent_name = typeof attrs.id === 'string' ? storedNames.get(attrs.id) : undefined;
      }
      const name = normalizeBriefFieldAttrs(attrs).talent_name;
      if (name) attrs.talent_name = name;
      else delete attrs.talent_name;
      result = { ...node, attrs };
    }
    return Array.isArray(node.content) ? { ...result, content: node.content.map(walk) } : result;
  };
  return JSON.stringify(walk(doc));
}

export interface BriefFieldSummary {
  attrs: BriefFieldAttrs;
  /** Index among the doc's top-level blocks. */
  index: number;
}

/** True when the parsed doc contains at least one `briefField` node. */
export function docHasBriefFields(doc: unknown): boolean {
  return listBriefFields(doc).length > 0;
}

/**
 * Every `briefField` node in document order. Fields only ever live at the top
 * level of the doc (the node's content spec forbids nesting), but the walk is
 * recursive so a malformed doc still reports what it holds.
 */
export function listBriefFields(doc: unknown): BriefFieldSummary[] {
  const out: BriefFieldSummary[] = [];
  const root = doc as DocNode | null;
  if (!root || typeof root !== 'object') return out;
  const blocks = Array.isArray(root.content) ? root.content : [];
  blocks.forEach((block, index) => {
    collect(block, index, out);
  });
  return out;
}

function collect(node: DocNode | null | undefined, index: number, out: BriefFieldSummary[]): void {
  if (!node || typeof node !== 'object') return;
  if (node.type === BRIEF_FIELD_NODE) {
    out.push({ attrs: normalizeBriefFieldAttrs(node.attrs), index });
    return;
  }
  if (Array.isArray(node.content)) {
    for (const child of node.content) collect(child, index, out);
  }
}

/** A field the client is allowed to see: switched on and tagged `client`. */
export function isClientVisibleField(attrs: BriefFieldAttrs): boolean {
  return attrs.key !== 'warmup' && attrs.enabled && attrs.audience === 'client';
}

/** First enabled, named talent field, with its client visibility. */
export function briefTalentName(doc: unknown): { name: string | null; clientVisible: boolean } {
  const field = listBriefFields(doc).find(({ attrs }) =>
    attrs.key === 'talent' && attrs.enabled && attrs.talent_name,
  );
  return field
    ? { name: field.attrs.talent_name, clientVisible: isClientVisibleField(field.attrs) }
    : { name: null, clientVisible: false };
}

/**
 * The projection. Returns a copy of `doc` with every `briefField` node that
 * is disabled or `internal` removed outright (not hidden: absent). Docs
 * without brief fields come back structurally identical. Never throws on odd
 * input; anything that is not a node tree is returned as is.
 */
export function projectBriefForClient<T>(doc: T): T {
  const root = doc as unknown as DocNode;
  // A root that is itself a briefField (malformed doc_json) has no parent to
  // strip it from; nothing it holds can be proven safe, so project to empty.
  if (root && typeof root === 'object' && root.type === BRIEF_FIELD_NODE) {
    return { type: 'doc', content: [] } as unknown as T;
  }
  return projectNode(root) as unknown as T;
}

function projectNode(node: DocNode): DocNode {
  if (!node || typeof node !== 'object') return node;
  if (!Array.isArray(node.content)) return node;
  const content: DocNode[] = [];
  for (const child of node.content) {
    if (child && typeof child === 'object' && child.type === BRIEF_FIELD_NODE) {
      if (!isClientVisibleField(normalizeBriefFieldAttrs(child.attrs))) continue;
    }
    content.push(projectNode(child));
  }
  return { ...node, content };
}

// ProseMirror node sizes can be derived from JSON without loading an editor
// schema. These are the only leaf nodes used by script and brief documents.
const LEAF_TYPES = new Set(['scriptHeaderRow', 'hardBreak', 'refImage']);

/** Return the ProseMirror nodeSize represented by a JSON node. */
export function nodeSizeFromJson(node: unknown): number {
  if (!node || typeof node !== 'object') return 0;
  const value = node as DocNode;
  if (value.type === 'text') return typeof value.text === 'string' ? value.text.length : 0;
  if (value.type && LEAF_TYPES.has(value.type)) return 1;
  const children = Array.isArray(value.content) ? value.content : [];
  return 2 + children.reduce((total, child) => total + nodeSizeFromJson(child), 0);
}

export interface StrippedSpan {
  from: number;
  to: number;
}

/** Raw top-level range occupied by one `briefField`, with its audience. */
export interface BriefFieldSpan {
  id: string;
  from: number;
  to: number;
  visible: boolean;
}

/** Raw top-level ranges occupied by every field, in document order. */
export function briefFieldSpans(doc: unknown): BriefFieldSpan[] {
  if (!doc || typeof doc !== 'object') return [];
  const content = Array.isArray((doc as DocNode).content) ? (doc as DocNode).content! : [];
  const spans: BriefFieldSpan[] = [];
  let from = 0;
  for (const child of content) {
    const size = nodeSizeFromJson(child);
    if (child?.type === BRIEF_FIELD_NODE) {
      const attrs = normalizeBriefFieldAttrs(child.attrs);
      spans.push({
        id: attrs.id,
        from,
        to: from + size,
        visible: isClientVisibleField(attrs),
      });
    }
    from += size;
  }
  return spans;
}

/** Raw top-level ranges occupied by fields excluded from the client document. */
export function strippedSpans(doc: unknown): StrippedSpan[] {
  return briefFieldSpans(doc)
    .filter((span) => !span.visible)
    .map(({ from, to }) => ({ from, to }));
}

/** Remove every whitespace character, for comparisons across flattenings. */
export function squashWhitespace(s: string): string {
  return s.replace(/\s+/g, '');
}

/**
 * Field spans and both position mappings for one document, computed once.
 * Route handlers deciding a whole list of threads should build this rather
 * than call the standalone map functions per thread, which rewalk the doc.
 */
export interface BriefAnchorMapper {
  spans: BriefFieldSpan[];
  /** Ids of the fields the client is allowed to see. */
  visibleIds: Set<string>;
  /** Id of the field containing `pos` (`from` inclusive, `to` exclusive). */
  fieldIdAt(pos: number): string | null;
  toProjected(from: number, to: number): { from: number; to: number } | null;
  fromProjected(from: number, to: number): { from: number; to: number };
}

export function createAnchorMapper(doc: unknown): BriefAnchorMapper {
  const spans = briefFieldSpans(doc);
  const stripped = spans.filter((span) => !span.visible);
  const visibleIds = new Set(
    spans.filter((span) => span.visible && span.id).map((span) => span.id),
  );

  const toPos = (pos: number): number | null => {
    let strippedSize = 0;
    for (const span of stripped) {
      if (span.from <= pos && pos < span.to) return null;
      if (span.to <= pos) strippedSize += span.to - span.from;
    }
    return pos - strippedSize;
  };

  const fromPos = (pos: number): number => {
    let rawPos = pos;
    for (const span of stripped) {
      if (span.from <= rawPos) rawPos += span.to - span.from;
    }
    return rawPos;
  };

  return {
    spans,
    visibleIds,
    fieldIdAt(pos) {
      for (const span of spans) {
        if (span.from <= pos && pos < span.to) return span.id || null;
      }
      return null;
    },
    toProjected(from, to) {
      const mappedFrom = toPos(from);
      const mappedTo = toPos(to);
      return mappedFrom === null || mappedTo === null
        ? null
        : { from: mappedFrom, to: mappedTo };
    },
    fromProjected(from, to) {
      return { from: fromPos(from), to: fromPos(to) };
    },
  };
}

/** Map a raw document position into its client-projected coordinates. */
export function mapPosToProjected(doc: unknown, pos: number): number | null {
  const mapped = createAnchorMapper(doc).toProjected(pos, pos);
  return mapped === null ? null : mapped.from;
}

/** Map a client-projected position back into raw document coordinates. */
export function mapPosFromProjected(doc: unknown, pos: number): number {
  return createAnchorMapper(doc).fromProjected(pos, pos).from;
}

export function mapAnchorToProjected(
  doc: unknown,
  from: number,
  to: number,
): { from: number; to: number } | null {
  return createAnchorMapper(doc).toProjected(from, to);
}

export function mapAnchorFromProjected(
  doc: unknown,
  from: number,
  to: number,
): { from: number; to: number } {
  return createAnchorMapper(doc).fromProjected(from, to);
}

/**
 * String-in, string-out projection for route handlers holding a raw
 * `doc_json` column. A doc with no brief fields is returned byte-identical so
 * ordinary scripts are untouched; unparseable input fails closed to an empty
 * document because its contents cannot be inspected safely.
 */
export function projectBriefJsonForClient(docJson: string): string {
  let doc: unknown;
  try {
    doc = JSON.parse(docJson);
  } catch {
    return EMPTY_DOC_JSON;
  }
  const root = doc as DocNode | null;
  if (root && typeof root === 'object' && root.type === BRIEF_FIELD_NODE) {
    return EMPTY_DOC_JSON;
  }
  if (!docHasBriefFields(doc)) return docJson;
  return JSON.stringify(projectBriefForClient(doc));
}

interface RehydrationBaseField {
  block: DocNode;
  attrs: BriefFieldAttrs;
  originalIndex: number;
}

/**
 * Line the base's top-level blocks up with the client's, in order: a field
 * is found by id, a non-field block by being unchanged. Returns base index →
 * out index for every block that was found. Monotone, so a block is never
 * matched behind an earlier one.
 */
function alignBaseToOut(baseBlocks: DocNode[], out: unknown[]): Map<number, number> {
  const outIds = out.map(fieldId);
  const outJson = out.map((block, index) => (outIds[index] === null ? JSON.stringify(block) : ''));
  const found = new Map<number, number>();
  let cursor = 0;
  baseBlocks.forEach((block, baseIndex) => {
    const id = fieldId(block);
    const json = id === null ? JSON.stringify(block) : '';
    for (let outIndex = cursor; outIndex < out.length; outIndex += 1) {
      const hit = id === null ? outIds[outIndex] === null && outJson[outIndex] === json : outIds[outIndex] === id;
      if (hit) {
        found.set(baseIndex, outIndex);
        cursor = outIndex + 1;
        return;
      }
    }
  });
  return found;
}

/** `children` with every `briefField` removed, at this level and below. */
function stripBriefFields(children: unknown[]): unknown[] {
  return children
    .filter((child) => {
      if (!child || typeof child !== 'object') return true;
      return (child as { type?: unknown }).type !== BRIEF_FIELD_NODE;
    })
    .map(withoutNestedBriefFields);
}

function withoutNestedBriefFields(node: unknown): unknown {
  if (!node || typeof node !== 'object') return node;
  const value = node as Record<string, unknown>;
  if (!Array.isArray(value.content)) return node;
  return { ...value, content: stripBriefFields(value.content) };
}

function fieldId(node: unknown): string | null {
  if (!node || typeof node !== 'object') return null;
  const value = node as DocNode;
  return value.type === BRIEF_FIELD_NODE
    ? normalizeBriefFieldAttrs(value.attrs).id
    : null;
}

/**
 * Rebuild the raw brief from the client's projected edit. Master field ids,
 * order, attributes and private content stay authoritative; the client may
 * only change visible field content and top-level blocks outside fields.
 *
 * Brief-only, by construction: a base without fields (an empty or corrupted
 * brief) yields the client's non-field blocks and no fields at all, so the
 * client can never invent a field set. Callers decide by `template_key`,
 * never by what the document happens to contain.
 */
export function rehydrateBriefFromClient(base: unknown, clientDoc: unknown): unknown {
  if (!base || typeof base !== 'object') return base;
  const baseRoot = base as DocNode;
  if (baseRoot.type === BRIEF_FIELD_NODE) return base;

  const baseBlocks = Array.isArray(baseRoot.content) ? baseRoot.content : [];
  const baseFields: RehydrationBaseField[] = [];
  const firstFieldById = new Map<string, RehydrationBaseField>();
  const fieldAtBaseIndex = new Map<number, RehydrationBaseField>();
  baseBlocks.forEach((block, originalIndex) => {
    if (!block || typeof block !== 'object' || block.type !== BRIEF_FIELD_NODE) return;
    const entry = {
      block,
      attrs: normalizeBriefFieldAttrs(block.attrs),
      originalIndex,
    };
    baseFields.push(entry);
    fieldAtBaseIndex.set(originalIndex, entry);
    if (!firstFieldById.has(entry.attrs.id)) firstFieldById.set(entry.attrs.id, entry);
  });

  const clientRoot = clientDoc && typeof clientDoc === 'object'
    ? clientDoc as { content?: unknown }
    : null;
  const clientBlocks = Array.isArray(clientRoot?.content) ? clientRoot.content : [];
  const emitted = new Set<RehydrationBaseField>();
  const out: unknown[] = [];

  for (const clientBlock of clientBlocks) {
    if (
      clientBlock &&
      typeof clientBlock === 'object' &&
      (clientBlock as { type?: unknown }).type === BRIEF_FIELD_NODE
    ) {
      const clientField = clientBlock as DocNode;
      const baseField = firstFieldById.get(normalizeBriefFieldAttrs(clientField.attrs).id);
      if (!baseField || !isClientVisibleField(baseField.attrs) || emitted.has(baseField)) continue;
      // The body is the client's, minus any field smuggled inside it: the
      // node's content spec forbids nesting, so one would make the stored doc
      // schema-invalid, and it would render as a section the client invented.
      const clientContent = Array.isArray(clientField.content)
        ? stripBriefFields(clientField.content)
        : [];
      const content = clientContent.length > 0 ? clientContent : baseField.block.content;
      out.push({ ...baseField.block, content });
      emitted.add(baseField);
      continue;
    }
    out.push(withoutNestedBriefFields(clientBlock));
  }

  // Put every base field the client did not send back where it was, next to
  // the nearest neighbouring block that can still be found in the client's
  // document: the closest earlier one, else the closest later one. A field
  // is found by id and prose by being unchanged, so a paragraph Henry wrote
  // between a visible field and a private one keeps the private field on
  // the side it was on, whether or not the client touched the prose around
  // it. A missing field that follows another missing field goes straight
  // after it, so a run of private fields keeps its order.
  const foundAt = alignBaseToOut(baseBlocks, out);
  type Anchor = { side: 'after' | 'before'; at: number }; // `at` is an out index; -1 = the start
  const anchorOfMissing = new Map<number, Anchor>();
  const before = new Map<number, unknown[]>();
  const after = new Map<number, unknown[]>();
  baseBlocks.forEach((block, baseIndex) => {
    const missing = fieldAtBaseIndex.get(baseIndex);
    if (!missing || emitted.has(missing)) return;
    let anchor: Anchor | null = null;
    for (let j = baseIndex - 1; j >= 0 && !anchor; j -= 1) {
      const earlier = anchorOfMissing.get(j);
      if (earlier) anchor = earlier;
      else if (foundAt.has(j)) anchor = { side: 'after', at: foundAt.get(j)! };
    }
    for (let j = baseIndex + 1; j < baseBlocks.length && !anchor; j += 1) {
      if (foundAt.has(j)) anchor = { side: 'before', at: foundAt.get(j)! };
    }
    if (!anchor) anchor = { side: 'after', at: -1 };
    anchorOfMissing.set(baseIndex, anchor);
    const bucket = anchor.side === 'after' ? after : before;
    bucket.set(anchor.at, [...(bucket.get(anchor.at) ?? []), block]);
    emitted.add(missing);
  });
  const placed: unknown[] = [...(after.get(-1) ?? [])];
  out.forEach((block, outIndex) => {
    placed.push(...(before.get(outIndex) ?? []), block, ...(after.get(outIndex) ?? []));
  });
  out.length = 0;
  out.push(...placed);

  // Treat field positions as slots and fill them in master order. This keeps
  // non-field blocks where the client placed them while preventing a forged
  // client document from reordering fields.
  const hydratedByBaseField = new Map<RehydrationBaseField, unknown>();
  for (const block of out) {
    const id = fieldId(block);
    if (id === null) continue;
    const baseField = baseFields.find((entry) => entry.attrs.id === id && !hydratedByBaseField.has(entry));
    if (baseField) hydratedByBaseField.set(baseField, block);
  }
  let fieldIndex = 0;
  const orderedOut = out.map((block) => {
    if (fieldId(block) === null) return block;
    const baseField = baseFields[fieldIndex++];
    return hydratedByBaseField.get(baseField) ?? baseField.block;
  });

  return { ...baseRoot, content: orderedOut };
}

/**
 * String form of brief rehydration for route handlers. Both documents must
 * parse and the client must send a document root. Call it only for a brief
 * (by `template_key`); it never passes a document through untouched.
 */
export function rehydrateBriefJsonFromClient(
  baseJson: string,
  clientJson: string,
): string | null {
  let base: unknown;
  let client: unknown;
  try {
    base = JSON.parse(baseJson);
    client = JSON.parse(clientJson);
  } catch {
    return null;
  }
  if (
    !client ||
    typeof client !== 'object' ||
    (client as { type?: unknown }).type !== 'doc'
  ) {
    return null;
  }
  return JSON.stringify(rehydrateBriefFromClient(base, client));
}

/**
 * Re-express a stored brief suggestion on the master document as it is now.
 *
 * A suggestion's `doc_json` carries the master's private fields as they were
 * at the client's last save. Master can move underneath it: Henry edits an
 * internal field, hides one, or flips a field's audience. Every read of a
 * suggestion goes through here first, so that the client's projection and
 * the admin's diff both take visibility, attributes and private content from
 * master now, and only the content of fields the client may see from the
 * suggestion. A field turned Internal after the suggestion started therefore
 * vanishes from the client's copy on the next poll, and Henry's later edit
 * to an internal field never shows up in the diff as a client deletion.
 *
 * Unreadable input falls back to `masterJson`: nothing of the suggestion can
 * be placed safely, and master is always client-safe once projected.
 */
export function realignBriefSuggestion(masterJson: string, suggestionJson: string): string {
  const clientSide = projectBriefJsonForClient(suggestionJson);
  return rehydrateBriefJsonFromClient(masterJson, clientSide) ?? masterJson;
}

/** Plain text of every node beneath `node`, for tests and summaries. */
export function briefFieldText(node: unknown): string {
  const parts: string[] = [];
  walkText(node as DocNode, parts);
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

function walkText(node: DocNode | null | undefined, out: string[]): void {
  if (!node || typeof node !== 'object') return;
  if (typeof node.text === 'string') out.push(node.text);
  if (Array.isArray(node.content)) for (const child of node.content) walkText(child, out);
}

// ---------------------------------------------------------------------------
// Seeding a new brief
// ---------------------------------------------------------------------------

interface SeedNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: SeedNode[];
  text?: string;
}

const para = (text = ''): SeedNode =>
  text
    ? { type: 'paragraph', content: [{ type: 'text', text }] }
    : { type: 'paragraph' };

const bullet = (text: string, cues: string[] = []): SeedNode => ({
  type: 'listItem',
  content: [
    para(text),
    ...(cues.length
      ? [{ type: 'bulletList', content: cues.map((c) => bullet(c)) }]
      : []),
  ],
});

function seedBody(def: BriefFieldDefinition): SeedNode[] {
  if (def.key === 'warmup') return warmupBody(chooseWarmups()) as SeedNode[];
  if (def.key === 'key_messages') {
    return [
      {
        type: 'bulletList',
        content: [
          bullet('The one thing they must remember.'),
          bullet('Why it is true, in their words.'),
          bullet('What they should do next.'),
        ],
      },
    ];
  }
  switch (def.body_kind) {
    case 'questions':
      return [
        {
          type: 'bulletList',
          content: [
            bullet(def.seed, ['Let them settle in; keep the first answer easy']),
            bullet('What was the situation before?', ['Push for the honest version']),
            bullet('What changed, and what does it look like now?'),
          ],
        },
      ];
    case 'rows':
      return [
        { type: 'scriptHeaderRow' },
        {
          type: 'scriptRow',
          content: [
            {
              type: 'scriptCell',
              attrs: { kind: 'audio', audioType: DEFAULT_AUDIO_TYPE },
              content: [para(def.seed)],
            },
            {
              type: 'scriptCell',
              attrs: { kind: 'visuals', visualType: DEFAULT_VISUAL_TYPE },
              content: [para('What we see while this plays.')],
            },
          ],
        },
      ];
    default:
      return [para(def.seed)];
  }
}

export function buildBriefFieldNode(
  def: BriefFieldDefinition,
  id: string = ulid(),
  body: SeedNode[] = seedBody(def),
) {
  const attrs: BriefFieldAttrs = {
    id,
    key: def.key,
    title: def.title,
    talent_name: '',
    audience: def.audience,
    enabled: def.defaultEnabled,
    shoot: def.defaultShoot,
    body_kind: def.body_kind,
  };
  return { type: BRIEF_FIELD_NODE, attrs, content: body };
}

/** Attrs for a brand-new custom field (appended to the end, switched on). */
export function customBriefFieldAttrs(
  title: string,
  audience: BriefAudience,
  id: string = ulid(),
): BriefFieldAttrs {
  return {
    id,
    key: CUSTOM_FIELD_KEY,
    title: title.trim(),
    talent_name: '',
    audience,
    enabled: true,
    shoot: false,
    body_kind: 'rich',
  };
}

/**
 * The initial document for a new interview brief:
 * `doc > heading(1 title) > labelPara(Client) > briefField*`, one node per
 * library entry (hidden ones included, so switching them on later restores
 * a seeded body). `makeId` is injectable so tests can pin ids.
 */
export function buildInterviewBriefDoc(
  input: { title: string; client_name: string; purpose?: string; talent?: string },
  makeId: () => string = ulid,
) {
  return {
    type: 'doc',
    content: [
      { type: 'heading', attrs: { level: 1 }, content: [{ type: 'text', text: input.title }] },
      {
        type: 'paragraph',
        content: [
          { type: 'text', marks: [{ type: 'bold' }], text: 'Client: ' },
          { type: 'text', text: input.client_name },
        ],
      },
      ...BRIEF_FIELD_LIBRARY.map((def) => def.key === 'warmup'
        ? buildBriefFieldNode(def, makeId(), warmupBody(chooseWarmups(input.purpose, input.talent)) as SeedNode[])
        : def.key === 'purpose' && input.purpose
          ? buildBriefFieldNode(def, makeId(), [para(input.purpose)])
        : def.key === 'talent' && input.talent
          ? buildBriefFieldNode(def, makeId(), [para(input.talent)])
        : buildBriefFieldNode(def, makeId())),
    ],
  };
}
